import { useState, useEffect, useCallback } from 'react';
import {
  Zap, TrendingUp, TrendingDown, Minus, RefreshCw, Clock, CheckCircle, XCircle, BarChart2,
  AlertTriangle,
} from 'lucide-react';
// AI Signal page decoupled from bot system — uses symbol-direct market data.
import { Card, CardHeader, Spinner, StatCard, InfoTip } from '../components/ui';
import * as api from '../services/api';

// No mock data — real API data only. Shows empty state when no bot is selected.

function SignalCard({ signal, confidence, reasons, probUp, probDown, riskLevel, expectedRange, window: win }) {
  const isActive = signal !== 'HOLD';
  const colors = { BUY: { bg: 'bg-emerald-500/10 border-emerald-500/30', text: 'text-emerald-400', icon: TrendingUp }, SELL: { bg: 'bg-red-500/10 border-red-500/30', text: 'text-red-400', icon: TrendingDown }, HOLD: { bg: 'bg-amber-500/10 border-amber-500/30', text: 'text-amber-400', icon: Minus } };
  const { bg, text, icon: Icon } = colors[signal] || colors.HOLD;

  // Circular confidence indicator
  const r = 40;
  const circ = 2 * Math.PI * r;
  const filled = (confidence / 100) * circ;

  return (
    <Card className={`border-2 ${bg}`}>
      <div className="flex flex-col md:flex-row gap-6 items-center">
        {/* Signal Badge */}
        <div className="flex flex-col items-center gap-2 min-w-[140px]">
          <div className="relative">
            <svg width="120" height="120" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r={r} fill="none" stroke="#1f2937" strokeWidth="10" />
              <circle cx="50" cy="50" r={r} fill="none" stroke={signal === 'BUY' ? '#22c55e' : signal === 'SELL' ? '#ef4444' : '#f59e0b'}
                strokeWidth="10" strokeDasharray={`${filled} ${circ}`} strokeDashoffset="0"
                transform="rotate(-90 50 50)" strokeLinecap="round" />
              <text x="50" y="42" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold">{signal}</text>
              <text x="50" y="58" textAnchor="middle" fill="#9ca3af" fontSize="14">{confidence}%</text>
              <text x="50" y="70" textAnchor="middle" fill="#6b7280" fontSize="9">confidence</text>
            </svg>
          </div>
          <Icon className={`w-6 h-6 ${text}`} />
        </div>

        {/* Signal Stats */}
        <div className="flex-1 grid grid-cols-2 gap-3">
          <div className="bg-dark-700 rounded-lg p-3">
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              Risk Level
              <InfoTip side="bottom">Current volatility regime: Low / Medium / High / Extreme. Based on ATR + trend strength + pattern clarity. High/Extreme = even a correct signal can have big drawdowns before being right. Adjust position sizing accordingly.</InfoTip>
            </p>
            <p className={`font-bold ${riskLevel === 'Low' ? 'text-emerald-400' : riskLevel === 'High' || riskLevel === 'Extreme' ? 'text-red-400' : 'text-amber-400'}`}>{riskLevel}</p>
          </div>
          <div className="bg-dark-700 rounded-lg p-3">
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              Expected Range
              <InfoTip side="bottom">The model's forecast cone over the signal window (e.g. -5% to +7%). NOT a guarantee — the actual move can fall outside this range in high-vol regimes. Treat as "what the model thinks is reasonable."</InfoTip>
            </p>
            <p className="text-white font-bold text-sm">{expectedRange?.low}% to +{expectedRange?.high}%</p>
          </div>
          <div className="bg-dark-700 rounded-lg p-3">
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              P(Up) / P(Down)
              <InfoTip side="bottom">Estimated probability that the next move is up vs down. P(Up) + P(Down) intentionally does NOT sum to 100% — the remainder is the HOLD probability (no clear direction).</InfoTip>
            </p>
            <p className="text-sm"><span className="text-emerald-400 font-bold">{probUp}%</span> / <span className="text-red-400 font-bold">{probDown}%</span></p>
          </div>
          <div className="bg-dark-700 rounded-lg p-3">
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              Window
              <InfoTip side="bottom">The forecast horizon for this signal (typically 24h). Expected Range and probabilities are calibrated to this window. Don't apply a 24h signal to a 1-hour trade decision.</InfoTip>
            </p>
            <p className="text-white font-bold">{win}</p>
          </div>
        </div>
      </div>
    </Card>
  );
}

const POPULAR_PAIRS = [
  'BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'BNB/USDT', 'XRP/USDT', 'DOGE/USDT',
  'ADA/USDT', 'AVAX/USDT', 'LINK/USDT', 'DOT/USDT', 'MATIC/USDT', 'ARB/USDT',
];

export default function SignalPage() {
  const [symbol, setSymbol] = useState('BTC/USDT');
  const [customSymbol, setCustomSymbol] = useState('');
  const [data, setData] = useState(null);
  const [history] = useState([]); // signal history requires per-symbol persistence — deferred
  const [performance] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!symbol) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.getMarketSignal(symbol);
      // Unwrap {success, data, timestamp} envelope from backend
      setData(res?.data || res);
    } catch (err) {
      setData({ dataSource: 'unavailable', reason: err?.message });
    } finally {
      setLoading(false);
    }
  }, [symbol]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, [fetchData]);

  function applyCustomSymbol() {
    const s = customSymbol.trim().toUpperCase();
    if (!s) return;
    setSymbol(s.includes('/') ? s : s + '/USDT');
    setCustomSymbol('');
  }

  const sig = data?.signal || {};
  const patterns = data?.patterns || [];
  const keyIndicators = data?.keyIndicators || [];
  const perf = performance || {};
  const dataUnavailable = data && data.dataSource && data.dataSource !== 'real';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-accent-primary">AI Signal Engine</h1>
          <p className="text-gray-400">Multi-indicator fusion model — BUY / SELL / HOLD signals with reasoning</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select value={symbol} onChange={e => setSymbol(e.target.value)} className="input w-auto text-sm">
            {POPULAR_PAIRS.map(p => <option key={p} value={p}>{p}</option>)}
            {!POPULAR_PAIRS.includes(symbol) && <option value={symbol}>{symbol}</option>}
          </select>
          <button onClick={fetchData} disabled={loading} className="btn-ghost p-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Custom symbol entry */}
      <div className="flex items-center gap-2 max-w-md">
        <input
          type="text" value={customSymbol}
          onChange={e => setCustomSymbol(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && applyCustomSymbol()}
          placeholder="Custom symbol — e.g. ATOM/USDT, ENS/USDT"
          className="flex-1 bg-dark-800 border border-dark-700 rounded-xl px-3 py-2 text-xs text-white placeholder-gray-600 focus:border-amber-500 focus:outline-none"
        />
        <button onClick={applyCustomSymbol} disabled={!customSymbol.trim()}
          className="px-3 py-2 rounded-xl text-xs font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 disabled:opacity-30">
          Load
        </button>
      </div>

      {/* Data unavailable notice */}
      {dataUnavailable && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-300">Signals unavailable for {symbol}</p>
            <p className="text-xs text-amber-300/80 mt-0.5">{data.message || data.reason || 'Real OHLCV data is required for signal generation. Try a major pair like BTC/USDT, ETH/USDT, SOL/USDT.'}</p>
          </div>
        </div>
      )}

      {/* Main Signal Card */}
      {sig.signal && !dataUnavailable && (
        <SignalCard
          signal={sig.signal}
          confidence={sig.confidence}
          reasons={sig.reasons}
          probUp={sig.probUp}
          probDown={sig.probDown}
          riskLevel={sig.riskLevel}
          expectedRange={sig.expectedRange}
          window={sig.window}
        />
      )}

      {/* Reasoning Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Signal Reasoning" subtitle="Why this signal was generated" />
          <div className="space-y-2">
            {(sig.reasons || []).length === 0 ? (
              <p className="text-xs text-gray-500 italic">No reasoning available — signal not generated yet.</p>
            ) : (sig.reasons || []).map((reason, i) => (
              <div key={i} className="flex items-start gap-3 text-sm">
                <span className="w-5 h-5 rounded-full bg-accent-primary/20 text-accent-primary text-xs flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-gray-300">{reason}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-dark-600">
            <p className="text-xs text-gray-400 mb-2">Key Indicators</p>
            <div className="flex flex-wrap gap-2">
              {keyIndicators.length === 0 ? (
                <span className="text-[11px] text-gray-500 italic">No active indicator signals — market neutral.</span>
              ) : keyIndicators.map((ind, i) => {
                const color =
                  ind.tone === 'bull' ? 'bg-emerald-500/20 text-emerald-400' :
                  ind.tone === 'bear' ? 'bg-red-500/20 text-red-400' :
                  ind.tone === 'warn' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-blue-500/20 text-blue-400';
                return <span key={`${ind.label}-${i}`} className={`text-xs px-2 py-0.5 rounded-full ${color}`}>{ind.label}</span>;
              })}
            </div>
          </div>
          {sig.generatedAt && (
            <p className="text-xs text-gray-500 mt-3">Generated: {new Date(sig.generatedAt).toLocaleTimeString()}</p>
          )}
        </Card>

        {/* Detected Patterns */}
        <Card>
          <CardHeader title="Detected Patterns" subtitle="Chart patterns identified by the AI engine" />
          <div className="space-y-3">
            {patterns.map((p, i) => (
              <div key={i} className="bg-dark-700 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-white text-sm">{p.pattern}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${p.riskLevel === 'low' ? 'bg-emerald-500/20 text-emerald-400' : p.riskLevel === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {p.riskLevel.toUpperCase()}
                    </span>
                    <span className={`text-xs font-bold ${p.direction === 'bullish' ? 'text-emerald-400' : p.direction === 'bearish' ? 'text-red-400' : 'text-amber-400'}`}>
                      {p.direction === 'bullish' ? '↑' : p.direction === 'bearish' ? '↓' : '→'}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-400">
                  <div>
                    <p>Confidence</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1.5 bg-dark-600 rounded-full"><div className="h-full bg-accent-primary rounded-full" style={{ width: `${p.confidence}%` }} /></div>
                      <span className="text-white">{p.confidence}%</span>
                    </div>
                  </div>
                  <div>
                    <p>Breakout Prob</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1.5 bg-dark-600 rounded-full"><div className="h-full bg-amber-500 rounded-full" style={{ width: `${p.breakoutProbability}%` }} /></div>
                      <span className="text-white">{p.breakoutProbability}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {patterns.length === 0 && <p className="text-gray-400 text-sm text-center py-4">No patterns detected</p>}
          </div>
        </Card>
      </div>

      {/* Performance Stats — only show metrics with real completed signals; pending state otherwise */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {perf.completedSignals > 0 ? (
          <>
            <StatCard title="Win Rate" value={`${perf.winRate}%`} icon={CheckCircle} iconColor="text-emerald-400" />
            <StatCard title="Avg Return" value={`${perf.avgReturn >= 0 ? '+' : ''}${perf.avgReturn}%`} icon={TrendingUp} iconColor="text-accent-primary" />
            <StatCard title="Max Drawdown" value={`-${perf.maxDrawdown}%`} icon={TrendingDown} iconColor="text-red-400" />
            <StatCard title="Total Signals" value={perf.totalSignals} icon={Zap} iconColor="text-amber-400" />
          </>
        ) : (
          <div className="col-span-2 lg:col-span-4 bg-dark-800 rounded-2xl border border-dark-700/50 p-5 flex items-start gap-3">
            <Clock className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-white">Performance pending</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {perf.totalSignals > 0
                  ? `${perf.totalSignals} signal${perf.totalSignals === 1 ? '' : 's'} generated, but none have completed their window yet. Win rate and avg return will appear once signals close.`
                  : `No signals generated for this bot yet. Win rate, average return, and drawdown will calculate from real completed signals as the engine produces them.`}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Signal History Table */}
      <Card>
        <CardHeader title="Signal History" subtitle="Last 14 days of generated signals" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-left border-b border-dark-600">
                <th className="pb-3 pr-4">Date & Time</th>
                <th className="pb-3 pr-4">Signal</th>
                <th className="pb-3 pr-4">Confidence</th>
                <th className="pb-3 pr-4">Risk</th>
                <th className="pb-3">Result</th>
              </tr>
            </thead>
            <tbody className="space-y-0">
              {history.slice(0, 15).map((h, i) => (
                <tr key={i} className="border-b border-dark-700 hover:bg-dark-700/50 transition-colors">
                  <td className="py-2.5 pr-4 text-gray-400 text-xs">
                    <div className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(h.date || h.generatedAt || h.savedAt).toLocaleString()}</div>
                  </td>
                  <td className="py-2.5 pr-4">
                    <span className={`font-bold text-sm ${h.signal === 'BUY' ? 'text-emerald-400' : h.signal === 'SELL' ? 'text-red-400' : 'text-amber-400'}`}>{h.signal}</span>
                  </td>
                  <td className="py-2.5 pr-4">
                    <div className="flex items-center gap-2">
                      <div className="w-20 h-1.5 bg-dark-600 rounded-full"><div className="h-full bg-accent-primary rounded-full" style={{ width: `${h.confidence}%` }} /></div>
                      <span className="text-gray-300 text-xs">{h.confidence}%</span>
                    </div>
                  </td>
                  <td className="py-2.5 pr-4">
                    <span className={`text-xs ${h.riskLevel === 'Low' ? 'text-emerald-400' : h.riskLevel === 'High' ? 'text-red-400' : 'text-amber-400'}`}>{h.riskLevel}</span>
                  </td>
                  <td className="py-2.5">
                    {h.result !== undefined && h.result !== null && h.signal !== 'HOLD' ? (
                      <span className={`font-bold text-sm ${h.result > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {h.result > 0 ? <CheckCircle className="w-3 h-3 inline mr-1" /> : <XCircle className="w-3 h-3 inline mr-1" />}
                        {h.result > 0 ? '+' : ''}{h.result?.toFixed ? h.result.toFixed(1) : h.result}%
                      </span>
                    ) : (
                      <span className="text-gray-500 text-xs">{h.signal === 'HOLD' ? 'N/A' : 'Pending'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
